import React from "react";
import { FaEye, FaEdit, FaTrash } from "react-icons/fa";
import Can from "../common/can";
import "./task.css";
import { TaskStatus, TaskPriority } from "../../constants/constants";

const STATUS_COLUMNS = [
  { id: TaskStatus.TODO, title: "To-Do", headerClass: "kanban-header-todo" },
  { id: TaskStatus.IN_PROGRESS, title: "In Progress", headerClass: "kanban-header-in-progress" },
  { id: TaskStatus.ON_HOLD, title: "On Hold", headerClass: "kanban-header-on-hold" },
  { id: TaskStatus.DONE, title: "Done", headerClass: "kanban-header-done" },
];

const priorityColor = {
  [TaskPriority.LOW]: "var(--success)",
  [TaskPriority.MEDIUM]: "var(--warning)",
  [TaskPriority.HIGH]: "var(--danger)",
  [TaskPriority.URGENT]: "var(--danger)",
};

export default function KanbanBoard({ tasks, onStatusChange, onViewTask, onEditTask, onDeleteTask }) {
  
  const handleDragStart = (e, taskId) => {
    e.dataTransfer.setData("taskId", taskId);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, newStatus) => {
    const taskId = e.dataTransfer.getData("taskId");
    if (!taskId) return;
    
    const task = tasks.find(t => t.id.toString() === taskId || t.task_id === taskId);
    if (task && task.status !== newStatus) {
      onStatusChange(task, newStatus);
    }
  };

  const getTasksByStatus = (statusId) => {
    return tasks.filter(t => t.status === statusId);
  };

  return (
    <div className="kanban-board" style={{ display: "flex", gap: "16px", overflowX: "auto", padding: "8px 0" }}>
      {STATUS_COLUMNS.map(col => {
        const colTasks = getTasksByStatus(col.id);
        
        return (
          <div 
            key={col.id} 
            className="kanban-column"
            style={{ 
              flex: "0 0 300px", 
              background: "var(--bg-main)", 
              borderRadius: "12px", 
              display: "flex", 
              flexDirection: "column",
              border: "1px solid var(--border-color)",
              maxHeight: "calc(100vh - 250px)"
            }}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, col.id)}
          >
            {/* Column Header */}
            <div className={`kanban-column-header ${col.headerClass}`}>
              <h4 style={{ margin: 0, fontSize: "14px", color: "var(--text-main)", fontWeight: 700 }}>{col.title}</h4>
              <span className="kanban-count-pill">
                {colTasks.length}
              </span>
            </div>

            {/* Tasks Area */}
            <div className="kanban-task-list" style={{ padding: "12px", overflowY: "auto", flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
              {colTasks.length === 0 ? (
                <div style={{ padding: "24px", textAlign: "center", color: "var(--text-muted)", fontSize: "13px", border: "1px dashed var(--border-color)", borderRadius: "8px" }}>
                  Drop tasks here
                </div>
              ) : (
                colTasks.map(task => (
                  <div 
                    key={task.id}
                    className="kanban-task-card"
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id)}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", alignItems: "center" }}>
                      <span style={{ fontSize: "12px", color: "var(--text-muted)", fontWeight: "600" }}>{task.task_id}</span>
                      <span style={{ 
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        fontSize: "11px", 
                        padding: "2px 8px", 
                        borderRadius: "12px", 
                        background: `${priorityColor[task.priority || TaskPriority.MEDIUM]}20`, 
                        color: priorityColor[task.priority || TaskPriority.MEDIUM],
                        fontWeight: "700",
                        textTransform: "capitalize"
                      }}>
                        <span className="kanban-priority-dot" style={{ backgroundColor: priorityColor[task.priority || TaskPriority.MEDIUM] }}></span>
                        {task.priority_display || task.priority || "Medium"}
                      </span>
                    </div>
                    
                    <h5 style={{ margin: "0 0 10px 0", fontSize: "14px", color: "var(--text-main)", fontWeight: 600, lineHeight: 1.4 }}>{task.title}</h5>
                    
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "12px", paddingTop: "8px", borderTop: "1px solid var(--border-color)" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                         <div className="kanban-avatar-pill" title={task.assigned_to_name || task.team_name || "Unassigned"}>
                           {task.assigned_to_name ? task.assigned_to_name.substring(0, 2).toUpperCase() : (task.team_name ? "TM" : "?")}
                         </div>
                         <span style={{ fontSize: "12px", color: "var(--text-muted)", maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                           {task.assigned_to_name || task.team_name || "Unassigned"}
                         </span>
                      </div>
                      
                      <div style={{ display: "flex", gap: "2px" }}>
                        <button className="kanban-action-btn view" onClick={() => onViewTask(task)} title="View"><FaEye /></button>
                        <Can perm="task.update">
                          <button className="kanban-action-btn edit" onClick={() => onEditTask(task)} title="Edit"><FaEdit /></button>
                        </Can>
                        <Can perm="task.delete">
                          <button className="kanban-action-btn delete" onClick={() => onDeleteTask(task)} title="Delete"><FaTrash /></button>
                        </Can>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
